// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { D3eService } from 'src/services/d3e-service';
import { IDeviceSetBare } from 'src/types/device-sets';
import { URL_PARAMS } from 'src/types/device';
import { SelectedOrgMethod } from 'src/shared/selectedOrg';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject } from 'rxjs';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-device-overview',
    templateUrl: './device.overview.component.html',
    styleUrls: ['./device.overview.component.scss', '../component.common.scss'],
    standalone: false
})

export class DeviceOverviewComponent implements OnInit, OnChanges {
  @Input( )deviceSet: IDeviceSetBare;
  @Input() refreshTrigger: number;
  @Input() selectedOrgId: string;

  loadingEndpoints$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  valueStyle = {
    display: 'flex',
    height: '20px',
    width: '25px',
    margin: '0',
    padding: '0'
  };
  largeValueStyle = {
    display: 'flex',
    height: '35px',
    width: '40px',
    margin: '10px 0 0 40px',
  };
  connected = 0;
  connectedParams = {};
  offline = 0;
  offlineParams = {};
  eligible = 0;
  eligibleParams = {};
  locked = 0;
  lockedParams = {};
  unconfigured = 0;
  unconfiguredParams = {};
  noProtection = 0;
  noProtectionParams = {};

  totalDevices = 0;

  dashboardOverviewLoaded = false;

  fEndpointOverview: string;
  fEndpointConnected: string;
  fEndpointOffline: string;
  fEndpointUpgrade: string;
  fEndpointNoProt: string;
  fEndpointUnconfig: string;
  fEndInfo: string;
constructor(
    private d3eService: D3eService,
    private translate: TranslateService,
  ) {
  this.translate.get(['DASH_END_OVER', 'DASH_END_CONNECTED', 'DASH_END_OFFLINE', 'DASH_END_UPGRADE', 'DASH_END_NOPROT',
    'DASH_END_UNCONFIG', 'DASH_END_INFO']).subscribe(vals => {
    this.fEndpointOverview = vals['DASH_END_OVER'];
    this.fEndpointConnected = vals['DASH_END_CONNECTED'];
    this.fEndpointOffline = vals['DASH_END_OFFLINE'];
    this.fEndpointUpgrade = vals['DASH_END_UPGRADE'];
    this.fEndpointNoProt = vals['DASH_END_NOPROT'];
    this.fEndpointUnconfig = vals['DASH_END_UNCONFIG'];
    this.fEndInfo = vals['DASH_END_INFO'];
  });
  this.loadingEndpoints$.next(true);
}

  ngOnInit() {
    if ( this.refreshTrigger == null ) {
      this.fEndInfo = '';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ( changes.deviceSet !== undefined ) {
      this.deviceSet = changes.deviceSet.currentValue;
    } 

    if ( changes.selectedOrgId !== undefined ) {
      this.selectedOrgId = changes.selectedOrgId.currentValue;
      if (this.refreshTrigger == null) {
          this.getDeviceOverview();
      }
    }

    if ( changes.refreshTrigger !== undefined && changes.refreshTrigger.firstChange === false) {
      this.refreshTrigger = changes.refreshTrigger.currentValue;
    }

    if ( this.refreshTrigger ) {
       this.getDeviceOverview();
    }
  }

  public getDeviceOverview() {
    this.loadingEndpoints$.next(true);
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
    const queryJson = { 'selectedOrgId': selectedOrg };
    let deviceSetId = null;

    queryJson['where'] = { 'deactivated': 0} ;

    if ( this.deviceSet !== undefined && this.deviceSet != null) {
      deviceSetId = this.deviceSet.id;
      queryJson['sets'] = [deviceSetId];
    }
    
    const params = {};
    if ( deviceSetId != null) {
      params['deviceSetId'] = deviceSetId.toString();
    }

    this.d3eService.get_home_device_overview(queryJson)
      .pipe(untilDestroyed(this))
      .subscribe(results => {
        this.connectedParams = JSON.parse(JSON.stringify(params));
        this.connectedParams[URL_PARAMS.OVERVIEW_PARAM_NAME] = URL_PARAMS.OVERVIEW_CONNECTED;
        this.offlineParams = JSON.parse(JSON.stringify(params));
        this.offlineParams[URL_PARAMS.OVERVIEW_PARAM_NAME] = URL_PARAMS.OVERVIEW_OFFLINE;
        this.eligibleParams = JSON.parse(JSON.stringify(params));
        this.eligibleParams[URL_PARAMS.OVERVIEW_PARAM_NAME] = URL_PARAMS.OVERVIEW_ELIGIBLE;
        this.lockedParams = JSON.parse(JSON.stringify(params));
        this.lockedParams[URL_PARAMS.OVERVIEW_PARAM_NAME] = URL_PARAMS.OVERVIEW_LOCKED;
        this.unconfiguredParams = JSON.parse(JSON.stringify(params));
        this.unconfiguredParams[URL_PARAMS.OVERVIEW_PARAM_NAME] = URL_PARAMS.OVERVIEW_UNCONFIGURED_SECURE;
        this.noProtectionParams = JSON.parse(JSON.stringify(params));
        this.noProtectionParams[URL_PARAMS.OVERVIEW_PARAM_NAME] = URL_PARAMS.OVERVIEW_NO_PROTECTION;

        this.connected = results['connected'];
        this.offline = results['offline'];
        this.eligible = results['eligible'];
        this.locked = results['locked'];
        this.unconfigured = results['unconfiguredSecureDrives'];
        this.noProtection = results['noProtection'];
        this.totalDevices = this.connected + this.offline;

        this.dashboardOverviewLoaded = true;
        this.loadingEndpoints$.next(false);
    },
    () => {
      this.loadingEndpoints$.next(false);
    });
  }

  getHeaderQueryParams() {
    const headerQueryParams = {};
    if ( this.deviceSet !== undefined && this.deviceSet != null ) {
      headerQueryParams['deviceSetId'] = this.deviceSet.id.toString();
    }
    return headerQueryParams;
  }
}
